import {useMutation} from '@tanstack/react-query';
import {useCallback, useEffect, useRef, useState} from 'react';
import {invenMutationAdapter} from '../../../../adapter/invenMutationAdapter';
import useTypedDispatch from '../../../../common/useTypedDispatch';
import useTypedSelector from '../../../../common/useTypedSelector';
import {ContentHomeCardSection} from '../../../../design-system/organisms/ContentHomeCardSection/ContentHomeCardSectionMain';
import {SummaryType} from '../../../../design-system/organisms/ContentHomeCardSection/ContentHomeSectionBody/SummaryType.enum';
import {ContentHomeTabWrapper} from '../../../../design-system/organisms/ContentHomeTabWrapper/ContentHomeTabWrapper';
import {MOBILE_MAIN_TAB_HEIGHT_PX} from '../../../../design-system/organisms/MainTab/constants';
import {ContentPreviewErrorBoundary} from '../../../../errors/ContentPreviewErrorBoudary.class';
import {SaleMethod} from '../../../../graphql/generated/types';
import {MOBILE_HEADER_HEIGHT_PX} from '../../../../header/constants';
import {aggregateAndSendLog} from '../../../../logging/aggregateAndSendLog';
import {ONLY_DATA_CONFIG} from '../../../../reactQuery/constant';
import {useLogin} from '../../../../user/useLogin.hook';
import {useContentHomeOverviewQuery} from '../../../../wrapper/contentWrapper';
import {useContentMyTicketQuery} from '../../../../wrapper/ticketWrapper';
import {CONTENT_HOME_UPPER_TAB_HEIGHT} from '../constant';
import {useContentMainTicketActionInfo} from '../hook/useContentMainTicketActionInfo';
import {ContentHomeLabelBadge} from '../presentational/ContentHomeLabelBadge';
import {
  setNeedAutoScroll,
  setNeedFetchOpenPreview,
} from '../state/contentHomeSlice';
import {createContentHomeLabelBadge} from '../util/createContentHomeLabelBadge';
import {ContentPreviewViewerContainer} from './ContentPreviewViewerContainer';

export function ContentPreviewMobileContainer({
  seriesId,
  productId,
}: {
  seriesId: number;
  productId: number;
}) {
  const overview = useContentHomeOverviewQuery({seriesId}, ONLY_DATA_CONFIG)
    .data?.contentHomeOverview;
  const {needAutoScroll, needFetchOpenPreview} = useTypedSelector(state => ({
    needAutoScroll: state.contentHome.needAutoScroll,
    needFetchOpenPreview: state.contentHome.needFetchOpenPreview,
  }));
  const isCashTypeHome = overview?.content?.saleMethod === SaleMethod.Cash;

  const {isLogin} = useLogin();
  const contentMyTicket = useContentMyTicketQuery(
    {
      seriesId,
      includeWaitfree: true,
    },
    {enabled: isLogin && !!overview && !isCashTypeHome}
  ).data?.contentMyTicket;

  const badgeList = createContentHomeLabelBadge({
    bm: overview?.content?.bm,
    ageGrade: overview?.content?.ageGrade,
    selfCensorship: overview?.content?.operatorProperty?.selfCensorship,
    onIssue: overview?.content?.onIssue,
    pubPeriod: overview?.content?.pubPeriod,
    waitfreePeriodByMinute: overview?.content?.waitfreePeriodByMinute,
    chargedPeriodByMinute: contentMyTicket?.waitfree?.chargedPeriodByMinute,
  });

  const {ticketActionInfo} = useContentMainTicketActionInfo({
    seriesId,
    isInPreviewTab: true,
  });
  const containerRef = useRef<HTMLDivElement | null>(null);
  const viewerWrapperRef = useRef<HTMLDivElement | null>(null);
  const textViewerRef = useRef<HTMLDivElement | null>(null);
  const dimmedAreaRef = useRef<HTMLDivElement | null>(null);

  const [expand, setExpand] = useState(false);
  const {mutate: mutateOpenPreview} = useMutation(
    invenMutationAdapter.openPreview()
  );
  const dispatch = useTypedDispatch();

  const prevRatioRef = useRef<number | null>(null);
  const timeoutRef = useRef(0);
  const prevIsDone = useRef<boolean | undefined>(ticketActionInfo?.isDone);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        window.clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (
      containerRef.current &&
      overview?.content?.serviceProperty?.preview?.usingScroll &&
      needAutoScroll
    ) {
      timeoutRef.current = window.setTimeout(() => {
        // 콘텐츠 홈 진입 시 0.4초 이후 한 번만 자동으로 스크롤합니다.
        const clientRect = containerRef.current?.getBoundingClientRect();
        if (clientRect) {
          window.scrollBy({
            top:
              clientRect.y -
              MOBILE_HEADER_HEIGHT_PX -
              MOBILE_MAIN_TAB_HEIGHT_PX,
            behavior: 'smooth',
          });
          dispatch(setNeedAutoScroll({needAutoScroll: false}));
        }
      }, 600);
    }
  }, [
    dispatch,
    needAutoScroll,
    overview?.content?.serviceProperty?.preview?.usingScroll,
  ]);

  useEffect(() => {
    if (!prevIsDone.current && ticketActionInfo?.isDone) {
      aggregateAndSendLog({
        actionType: 'Event',
        actionName: '첫화보기_스크롤',
        customProps: {
          user_action: 'complete',
        },
        click: {
          layer1: '첫화보기',
        },
      });
    }

    prevIsDone.current = ticketActionInfo?.isDone;
  }, [ticketActionInfo?.isDone]);

  const onScroll = useCallback(() => {
    if (!containerRef?.current || !viewerWrapperRef?.current) {
      return;
    }

    // containerTop: 컨텐츠 영역 (DA + 줄거리 + 뷰어) 최상단 Y좌표 (현재 뷰포트 기준)
    const {top: containerTop} = containerRef.current.getBoundingClientRect();
    // viewerTop: 뷰어 영역 최상단 Y좌표
    const {top: viewerTop} = viewerWrapperRef.current.getBoundingClientRect();

    // 뷰어 영역과 상단 탭 간의 Y좌표 차이를 기준으로 ratio를 계산합니다.
    // 계산식: (뷰어 영역과 상단 탭 간의 Y좌표 차이) / (뷰어 영역과 컨텐츠 영역 최상단 간의 Y좌표 차이)
    const upperTabTop = MOBILE_HEADER_HEIGHT_PX + CONTENT_HOME_UPPER_TAB_HEIGHT;
    const ratio = Math.max(
      Math.min((viewerTop - upperTabTop) / (viewerTop - containerTop), 1),
      0
    );
    const nextPadding = 11 * ratio;
    const nextRounding = 12 * ratio;

    viewerWrapperRef.current.style.paddingLeft = `${nextPadding}px`;
    viewerWrapperRef.current.style.paddingRight = `${nextPadding}px`;

    const child = viewerWrapperRef.current.firstElementChild;
    if (child) {
      (child as HTMLDivElement).style.borderTopLeftRadius = `${nextRounding}px`;
      (child as HTMLDivElement).style.borderTopRightRadius =
        `${nextRounding}px`;
    }

    if (dimmedAreaRef.current) {
      dimmedAreaRef.current.style.opacity = String(ratio);
    }

    // 텍스트뷰어의 경우 뷰어 영역이 커져도 컨텐츠가 리사이징 되지 않도록 (redraw 방지) 마진을 추가합니다.
    if (textViewerRef.current) {
      textViewerRef.current.style.marginLeft = `${11 - nextPadding}px`;
      textViewerRef.current.style.marginRight = `${11 - nextPadding}px`;
    }

    if (ratio === 0) {
      // 뷰어 영역이 최상위에 도달했을 경우 최초 1회에 한해 open_preview API를 호출합니다.
      if (needFetchOpenPreview) {
        dispatch(setNeedFetchOpenPreview({needFetchOpenPreview: false}));
        mutateOpenPreview({
          input: {
            seriesId,
            productId,
          },
        });
        aggregateAndSendLog({
          actionType: 'Event',
          actionName: '첫화보기_오픈',
          customProps: {
            user_action: 'scroll',
          },
          click: {
            layer1: '첫화보기',
          },
        });
      }

      if (prevRatioRef.current !== ratio) {
        aggregateAndSendLog({
          actionType: 'Event',
          actionName: '첫화보기_스크롤업',
          customProps: {
            user_action: 'scroll',
          },
          click: {
            layer1: '첫화보기',
          },
        });
      }
    }

    prevRatioRef.current = ratio;
  }, [dispatch, mutateOpenPreview, needFetchOpenPreview, productId, seriesId]);

  useEffect(() => {
    window.addEventListener('scroll', onScroll);

    return () => {
      window.removeEventListener('scroll', onScroll);
    };
  }, [onScroll]);

  return (
    <div ref={containerRef}>
      <ContentHomeTabWrapper
        isMobile={true}
        displayAd={overview?.displayAd}
        saleState={overview?.content?.saleState}
        className="flex min-h-screen flex-col space-y-8pxr pb-0"
      >
        {overview?.content?.description && (
          <div className="w-full px-11pxr">
            <ContentHomeCardSection>
              <ContentHomeCardSection.Header>
                <ContentHomeCardSection.Header.BadgeList>
                  {badgeList &&
                    badgeList.map((badge, index) => (
                      <ContentHomeLabelBadge
                        key={index}
                        labelBadge={badge}
                        className="mb-4pxr"
                      />
                    ))}
                </ContentHomeCardSection.Header.BadgeList>
              </ContentHomeCardSection.Header>
              <ContentHomeCardSection.Body>
                <ContentHomeCardSection.Body.Summary
                  type={SummaryType.Preview}
                  expand={expand}
                  isMobile={true}
                  onClickExpand={() => setExpand(true)}
                >
                  {overview.content.description}
                </ContentHomeCardSection.Body.Summary>
              </ContentHomeCardSection.Body>
            </ContentHomeCardSection>
          </div>
        )}
        <div className="flex min-h-[328px] w-full flex-col items-center justify-center">
          <ContentPreviewErrorBoundary>
            <ContentPreviewViewerContainer
              seriesId={seriesId}
              productId={productId}
              viewerWrapperRef={viewerWrapperRef}
              textViewerRef={textViewerRef}
              dimmedAreaRef={dimmedAreaRef}
            />
          </ContentPreviewErrorBoundary>
        </div>
      </ContentHomeTabWrapper>
    </div>
  );
}
